import { LanguageCode } from '@hazadapt-git/public-core-base'
import { Typography } from '@mui/material'
import React from 'react'

import PrepCheckActions from '../assets/icons/PrepCheckActions.svg'
import PrepCheckItems from '../assets/icons/PrepCheckItems.svg'
import PrepCheckContacts from '../assets/icons/PrepCheckContacts.svg'
import { Markdown } from '../components/atoms/Markdown'

export interface PrepCheckWelcomeTextConfig {
    title: string
    blurbChunk1: string
    blurbChunk2: string
    aspectDisplay: React.ReactNode
    blurbChunk3: string
    learnMoreBlurb: string
    guestModeBlurb: string
    continueCta: string
    signupCta: string
}

interface AspectDisplayItem {
    index: number
    text: string
    imageSrc: string
    imageAlt?: string
}

const renderAspectDisplayItem = (
    item: AspectDisplayItem,
    index: number
): React.ReactNode => {
    const textWithSeparateBoldLine = item.text.replace(
        /(\*\*.+?\*\*)\s*/,
        '$1<br />'
    )

    return (
        <div
            style={{
                display: 'grid',
                gridTemplateColumns: '3rem 1.5rem 1fr',
                gap: '1rem',
                alignItems: 'center',
            }}
            key={`aspectDisplayItem_${index + 1}`}
        >
            <img
                src={item.imageSrc}
                style={{ width: '3rem', height: 'auto' }}
                alt={item.imageAlt}
            />
            <Typography
                variant="h1"
                fontWeight={500}
                fontSize="2.5rem"
                textAlign="center"
            >
                {item.index}
            </Typography>
            <Markdown content={textWithSeparateBoldLine} />
        </div>
    )
}

const renderAspectDisplay = (items: AspectDisplayItem[]): React.ReactNode => (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
        {items.map(renderAspectDisplayItem)}
    </div>
)

export const prepCheckWelcomeText: Map<
    LanguageCode,
    PrepCheckWelcomeTextConfig
> = new Map<LanguageCode, PrepCheckWelcomeTextConfig>([
    [
        LanguageCode.ENGLISH,
        {
            title: `### Prep Checks: How Ready Are You?`,
            blurbChunk1: `Prep Checks help you answer one key question: _"How well could I handle this emergency?"_`,
            blurbChunk2: `This quick self-assessment shows where you’re strong and where you can grow in three areas:`,
            aspectDisplay: renderAspectDisplay([
                {
                    index: 1,
                    text: `**What you know** Actions to prepare, stay safe, and recover`,
                    imageSrc: PrepCheckActions,
                    imageAlt: 'Person running',
                },
                {
                    index: 2,
                    text: `**What you have** Emergency supplies you can access`,
                    imageSrc: PrepCheckItems,
                    imageAlt: 'First aid kit',
                },
                {
                    index: 3,
                    text: `**Who you can call** People you trust for support before, during, or after`,
                    imageSrc: PrepCheckContacts,
                    imageAlt: 'Telephone',
                },
            ]),
            blurbChunk3: `This is not a test to prove you know the “right” answer.  Simply answer what is most true to you and your life.`,
            learnMoreBlurb: `Learn More About Coping Capacity And The Research Behind Prep Checks`,
            guestModeBlurb: `**Guest Mode**: Your progress will be saved on this device only. Create your HazID Account and sign in to save your results to all your Prep Checks across devices.`,
            continueCta: 'Start Prep Checks',
            signupCta: 'Make HazID Account',
        },
    ],
    [
        LanguageCode.SPANISH,
        {
            title: `### Prep Checks: ¿Qué tan preparado estás?`,
            blurbChunk1: `Las Prep Checks te ayudan a responder una pregunta clave: _"¿Qué tan bien podría manejar esta emergencia?"_`,
            blurbChunk2: `Esta rápida autoevaluación muestra dónde eres fuerte y dónde puedes crecer en tres áreas:`,
            aspectDisplay: renderAspectDisplay([
                {
                    index: 1,
                    text: `**Lo que sabes** Acciones para prepararse, mantenerse seguro y recuperarse`,
                    imageSrc: PrepCheckActions,
                    imageAlt: 'Persona corriendo',
                },
                {
                    index: 2,
                    text: `**Lo que tienes** Suministros de emergencia a los que puedes acceder`,
                    imageSrc: PrepCheckItems,
                    imageAlt: 'Botiquín de primeros auxilios',
                },
                {
                    index: 3,
                    text: `**A quién puedes llamar** Personas en las que confías para recibir apoyo antes, durante o después`,
                    imageSrc: PrepCheckContacts,
                    imageAlt: 'Teléfono',
                },
            ]),
            blurbChunk3: `Esto no es una prueba para demostrar que sabes la "respuesta correcta".  Simplemente responde lo que es más verdadero para ti y tu vida.`,
            learnMoreBlurb: `Descubre más sobre la capacidad de afrontamiento y la investigación detrás de las Prep Checks`,
            guestModeBlurb: `**Modo invitado**: Tu progreso se guardará solo en este dispositivo. Crea tu cuenta HazID e inicia sesión para guardar tus resultados en todas tus Prep Checks entre dispositivos.`,
            continueCta: 'Iniciar Prep Checks',
            signupCta: 'Crear cuenta HazID',
        },
    ],
    [
        LanguageCode.GERMAN,
        {
            title: `### Prep Checks: Wie bereit bist du?`,
            blurbChunk1: `Prep Checks helfen Ihnen, eine wichtige Frage zu beantworten: _"Wie gut könnte ich diesen Notfall bewältigen?"_`,
            blurbChunk2: `Diese kurze Selbsteinschätzung zeigt, wo du stark bist und wo du in drei Bereichen wachsen kannst:`,
            aspectDisplay: renderAspectDisplay([
                {
                    index: 1,
                    text: `**Was du weißt** Maßnahmen zur Vorbereitung, Sicherheit und Genesung`,
                    imageSrc: PrepCheckActions,
                    imageAlt: 'Person, die läuft',
                },
                {
                    index: 2,
                    text: `**Was du hast** Notfallvorräte, auf die du zugreifen kannst`,
                    imageSrc: PrepCheckItems,
                    imageAlt: 'Erste-Hilfe-Set',
                },
                {
                    index: 3,
                    text: `**Wen du anrufen kannst** Menschen, denen du vor, während oder nach der Unterstützung vertraust`,
                    imageSrc: PrepCheckContacts,
                    imageAlt: 'Telefon',
                },
            ]),
            blurbChunk3: `Das ist kein Test, um zu beweisen, dass du die "richtige" Antwort kennst.  Beantworte einfach das, was für dich und dein Leben am wahrsten ist.`,
            learnMoreBlurb: `Erfahren Sie mehr über Bewältigungsfähigkeit und die Forschung hinter Prep Checks`,
            guestModeBlurb: `**Gastmodus**: Dein Fortschritt wird nur auf diesem Gerät gespeichert. Erstellen Sie Ihr HazID-Konto und melden Sie sich an, um Ihre Ergebnisse auf allen Ihren Prep Checks auf verschiedenen Geräten zu speichern.`,
            continueCta: 'Prep Checks starten',
            signupCta: 'HazID-Konto erstellen',
        },
    ],
    [
        LanguageCode.FRENCH,
        {
            title: `### Prep Checks: À quel point êtes-vous prêt ?`,
            blurbChunk1: `Les Prep Checks vous aident à répondre à une question clé : _"Comment pourrais-je gérer cette urgence?"_
`,
            blurbChunk2: `Cette rapide auto-évaluation montre où vous êtes fort et où vous pouvez progresser dans trois domaines :`,
            aspectDisplay: renderAspectDisplay([
                {
                    index: 1,
                    text: `**Ce que tu sais** Actions pour se préparer, rester en sécurité et récupérer`,
                    imageSrc: PrepCheckActions,
                    imageAlt: 'Personne qui court',
                },
                {
                    index: 2,
                    text: `**Ce que tu as** Fournitures d’urgence accessibles`,
                    imageSrc: PrepCheckItems,
                    imageAlt: 'Trousse de premiers soins',
                },
                {
                    index: 3,
                    text: `**Qui tu peux appeler** Des personnes en qui vous avez confiance pour obtenir du soutien avant, pendant ou après`,
                    imageSrc: PrepCheckContacts,
                    imageAlt: 'Téléphone',
                },
            ]),
            blurbChunk3: `Ce n’est pas un test pour prouver que vous connaissez la "bonne" réponse.  Répondez simplement à ce qui est le plus vrai pour vous et votre vie.`,
            learnMoreBlurb: `En savoir plus sur la capacité d’adaptation et la recherche derrière les Prep Checks`,
            guestModeBlurb: `**Mode invité** : Votre progression sera enregistrée uniquement sur cet appareil. Créez votre compte HazID et connectez-vous pour enregistrer vos résultats sur tous vos Prep Checks sur tous les appareils.`,
            continueCta: `Démarrer les Prep Checks`,
            signupCta: `Créer un compte HazID`,
        },
    ],
    [
        LanguageCode.MANDARIN,
        {
            title: `### Prep Checks：你准备好了吗？`,
            blurbChunk1: `Prep Checks帮助你回答一个关键问题：_“我能多好地应对这次紧急情况？”_
`,
            blurbChunk2: `这份简短的自我评估展示了你在三个方面的优势和成长空间：`,
            aspectDisplay: renderAspectDisplay([
                {
                    index: 1,
                    text: `**你所知道的**准备、保持安全和恢复的行动`,
                    imageSrc: PrepCheckActions,
                    imageAlt: '跑步者',
                },
                {
                    index: 2,
                    text: `**你拥有的**你可以获得的应急物资`,
                    imageSrc: PrepCheckItems,
                    imageAlt: '急救箱',
                },
                {
                    index: 3,
                    text: `**你可以打电话给谁**你信任的人，在之前、期间或之后给予支持
`,
                    imageSrc: PrepCheckContacts,
                    imageAlt: '电话',
                },
            ]),
            blurbChunk3: `这不是用来证明你知道“正确”答案的测试。 只需回答最符合你和你生活的答案。`,
            learnMoreBlurb: `了解更多关于应对能力及Prep Checks背后研究的信息`,
            guestModeBlurb: `**访客模式**：你的进度仅会保存在本设备上。创建您的HazID账户并登录，以便将结果保存到所有设备间的Prep Checks中。
`,
            continueCta: '开始Prep Checks',
            signupCta: '创建HazID账户',
        },
    ],
    [
        LanguageCode.RUSSIAN,
        {
            title: `### Prep Checks: насколько вы готовы?`,
            blurbChunk1: `Prep Checks помогает ответить на один ключевой вопрос: _"Насколько хорошо я смогу справиться с этой чрезвычайной ситуацией?"_
`,
            blurbChunk2: `Эта быстрая самооценка показывает, где вы сильны и где можете развиваться в трёх областях:`,
            aspectDisplay: renderAspectDisplay([
                {
                    index: 1,
                    text: `**Что ты знаешь** Действия по подготовке, безопасности и восстановлению`,
                    imageSrc: PrepCheckActions,
                    imageAlt: 'Бегущий человек',
                },
                {
                    index: 2,
                    text: `**То, что у тебя есть** Экстренные припасы, к которым можно получить доступ`,
                    imageSrc: PrepCheckItems,
                    imageAlt: 'Аптечка первой помощи',
                },
                {
                    index: 3,
                    text: `**Кому ты можешь позвонить** Люди, которым вы доверяете для поддержки до, во время или после`,
                    imageSrc: PrepCheckContacts,
                    imageAlt: 'Телефон',
                },
            ]),
            blurbChunk3: `Это не тест, чтобы доказать, что вы знаете "правильный" ответ.  Просто ответьте на то, что наиболее верно для вас и вашей жизни.
`,
            learnMoreBlurb: `Узнайте больше о способностях справляться с ситуацией и исследованиях, лежащих в основе Prep Checks`,
            guestModeBlurb: `**остевой режим**: Ваш прогресс будет сохранен только на этом устройстве. Создайте свой аккаунт HazID и войдите в систему, чтобы сохранить результаты на всех ваших Prep Checks на разных устройствах.`,
            continueCta: 'Начать Prep Checks',
            signupCta: 'Создать аккаунт HazID',
        },
    ],
    [
        LanguageCode.JAPANESE,
        {
            title: `### Prep Checks:準備はどれくらい?`,
            blurbChunk1: `Prep Checksは、重要な質問に答えるのに役立ちます。_「この緊急事態にどれだけうまく対応できるか?」_`,
            blurbChunk2: `この簡単な自己評価は、あなたがどこに強みを持ち、成長できるかを3つの分野で示しています。`,
            aspectDisplay: renderAspectDisplay([
                {
                    index: 1,
                    text: `**君が知っていること**準備、安全確保、回復のための行動`,
                    imageSrc: PrepCheckActions,
                    imageAlt: '走っている人',
                },
                {
                    index: 2,
                    text: `**君が持っているもの**緊急用品はアクセス可能です`,
                    imageSrc: PrepCheckItems,
                    imageAlt: '応急処置キット',
                },
                {
                    index: 3,
                    text: `**誰に電話できるか**前も最中も後も、信頼できる人たちの支えを求めてください`,
                    imageSrc: PrepCheckContacts,
                    imageAlt: '電話',
                },
            ]),
            blurbChunk3: `これは「正しい」答えを知っているかどうかを証明するためのテストではありません。 あなた自身とあなたの人生に最も真実なものを単純に答えてください。`,
            learnMoreBlurb: `対処能力とPrep Checksの背後にある研究について詳しく学ぶ`,
            guestModeBlurb: `**ゲストモード**:進行状況はこのデバイスにのみ保存されます。HazIDアカウントを作成しサインインすると、すべてのデバイス間のPrep Checkに結果を保存できます。`,
            continueCta: 'Prep Checks開始',
            signupCta: 'HazIDアカウント作成',
        },
    ],
])
